import NiceModal, { useModal } from '@ebay/nice-modal-react';

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import Button from 'components/field/Button';
import ProgressWizard from 'components/field/ProgressWizard';
import Alert from 'components/global/Alert';
import Card from 'components/global/Card';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import DynamicList from 'components/list/DynamicList';
import { FilterType, ListComponents } from 'components/list/DynamicListFilterUtils';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { SALES_CHANNELS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

import { faAngleLeft, faAngleRight, faFloppyDisk, faMapMarkerAlt, faWarehouse } from '@fortawesome/pro-solid-svg-icons';

import * as S from './StoreLocationForm.styled';

const StoreLocationForm = NiceModal.create(({ credentialsID }) => {
	const modal = useModal();
	const axiosPrivate = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();

	const [tab, setTab] = useState(0);
	const [selectedStoreLocation, setSelectedStoreLocation] = useState([]);
	const [selectedWarehouseAndBranchLocation, setSelectedWarehouseAndBranchLocation] = useState([]);
	const [locationData, setLocationData] = useState();

	const getLocationMapping = useCallback(async () => {
		try {
			const response = await axiosPrivate.post(SALES_CHANNELS.GET_LOCATION_MAPPING, {
				ID: credentialsID
			});

			if (response.status === 200) {
				setLocationData(response.data.locations);
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			showError({
				message: error.response?.data.code || error.response?.data.message
			});
			AppLogger.error(error);
		}
	}, [credentialsID]);

	useEffect(() => {
		if (credentialsID) getLocationMapping();
	}, [credentialsID]);

	const saveLocationMapping = async () => {
		const pickupLocationIDsArr = selectedWarehouseAndBranchLocation.map((item) => ({
			ID: item.id,
			type: item.type
		}));

		try {
			const response = await axiosPrivate.post(SALES_CHANNELS.SAVE_LOCATION_MAPPING, {
				ID: credentialsID,
				locationID: selectedStoreLocation.length > 0 ? selectedStoreLocation[0].id : null,
				pickupLocationIDs: pickupLocationIDsArr
			});

			if (response.status === 200) {
				showSuccess({
					header: 'locationMappingSaved',
					message: 'locationMappingSavedMessage'
				});
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			showError({
				message: error.response?.data.code || error.response?.data.message
			});
			AppLogger.error(error);
		}

		modal.remove();
	};

	const nextStep = () => {
		setTab(tab + 1);
	};

	const prevStep = () => {
		if (tab > 0) {
			setTab(tab - 1);
		}
	};

	return (
		<Modal maxWidth="xx-large" fullwidth className="modal-storeLocation">
			<ModalTitle>
				<Typography variant="p1">matchPickupLocations</Typography>
			</ModalTitle>

			<ModalContent>
				<ProgressWizard
					stages={[
						{ name: 'selectPickupLocationsInSalesChannel', icon: faMapMarkerAlt },
						{ name: 'selectLocationsInOto', icon: faWarehouse }
					]}
					setIndex={setTab}
					index={tab}
					className="mb-3"
				/>

				<Alert content="storeLocationInfo" className="mb-3" showIcon />

				<S.ListingArea>
					{tab === 0 && (
						<StoreLocationList
							selectedStoreLocation={selectedStoreLocation}
							setSelectedStoreLocation={setSelectedStoreLocation}
							locationData={locationData}
						/>
					)}
					{tab === 1 && (
						<WarehouseLocationList
							setSelectedWarehouseAndBranchLocation={setSelectedWarehouseAndBranchLocation}
						/>
					)}
				</S.ListingArea>
			</ModalContent>

			<ModalActions>
				<Button variant="outline" label="cancel" onClick={() => modal.remove()} />

				{tab === 1 && <Button onClick={prevStep} icon={faAngleLeft} label="previous" variant="outline" />}

				{tab === 0 && (
					<Button
						onClick={nextStep}
						iconPos="right"
						disabled={selectedStoreLocation?.length === 0}
						disabledTooltip={selectedStoreLocation?.length === 0 && 'formInfo.pleaseSelectStoreLocation'}
						icon={faAngleRight}
						label="continue"
						variant="blue"
					/>
				)}

				{tab === 1 && (
					<Button
						onClick={saveLocationMapping}
						disabled={selectedWarehouseAndBranchLocation?.length === 0}
						disabledTooltip={
							selectedWarehouseAndBranchLocation?.length === 0 &&
							'formInfo.pleaseSelectStoreLocationAndWarehouseORBranchLocation'
						}
						icon={faFloppyDisk}
						label="save"
						variant="blue"
					/>
				)}
			</ModalActions>
		</Modal>
	);
});

export default StoreLocationForm;

const StoreLocationList = ({ selectedStoreLocation, setSelectedStoreLocation, locationData }) => {
	const columns = React.useMemo(
		() => [
			{
				header: 'id',
				accessorKey: 'id',
				size: 100
			},
			{
				header: 'locationName',
				accessorKey: 'locationName',
				showOnMobile: true,
				size: 150
			},
			{
				header: 'address',
				accessorKey: 'address',
				showOnMobile: true,
				size: 400
			}
		],
		[]
	);

	return (
		<Card rounded>
			<Table
				columns={columns}
				data={locationData}
				enableRowSelection
				selectedRows={selectedStoreLocation}
				onSelectedRowsChange={setSelectedStoreLocation}
				enableMultiRowSelection={false}
			/>
		</Card>
	);
};

const WarehouseLocationList = ({ setSelectedWarehouseAndBranchLocation }) => {
	const columns = useMemo(
		() => [
			{ Header: 'id', accessor: 'id', filterType: FilterType.number, show: false },
			{ Header: 'name', accessor: 'name', filterType: FilterType.string, showOnMobile: true },
			{ Header: 'code', accessor: 'code', filterType: FilterType.string, showOnMobile: true },
			{ Header: 'address', accessor: 'address', filterType: FilterType.string },
			{ Header: 'type', accessor: 'type', filterType: FilterType.multiSelection },
			{
				Header: 'status',
				accessor: 'status',
				renderCell: ListComponents.STATUS,
				width: 100,
				filterType: FilterType.multiSelection
			}
		],
		[]
	);

	return (
		<div>
			<DynamicList
				id={DYNAMICLISTS.STORE_LOCATION_LIST}
				columns={columns}
				fetchUrl={SALES_CHANNELS.GET_WAREHOUSE_AND_BRANCH_LIST}
				exportName="warehousesAndBranches"
				showHeader={false}
				showCheckbox={true}
				onSelectionChange={(selectedRows) => {
					setSelectedWarehouseAndBranchLocation(selectedRows);
				}}
			/>
		</div>
	);
};
